import React, { useMemo } from 'react';
import { Users } from 'lucide-react';

/**
 * Compact read-only display of collaborator avatars/names for cards/rows.
 * Props:
 *  - ids: string[] of user_ids
 *  - users: Array<{ id, name, role }> full team list
 *  - max: max avatars shown before summarising (default 3)
 *  - label: optional prefix like "Shared with"
 *  - size: "sm" | "xs"
 */
export default function CollaboratorChips({
  ids = [],
  users = [],
  max = 3,
  label = 'Shared with',
  size = 'sm',
}) {
  const byId = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);
  const items = (ids || []).map((id) => byId.get(id)).filter(Boolean);
  if (items.length === 0) return null;

  const initials = (name) => (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  const visible = items.slice(0, max);
  const overflow = items.length - visible.length;
  const dot = size === 'xs' ? 'h-4 w-4 text-[9px]' : 'h-5 w-5 text-[10px]';

  return (
    <div className="flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="collab-chips">
      <Users className="h-3 w-3 text-amber-300 flex-shrink-0" />
      <span className="text-slate-500 shrink-0">{label}</span>
      <div className="flex -space-x-1.5">
        {visible.map((u) => (
          <span
            key={u.id}
            title={`${u.name} · ${u.role}`}
            className={`inline-flex ${dot} rounded-full items-center justify-center font-bold text-amber-100 bg-amber-500/25 border border-[#0a1628] ring-1 ring-amber-500/40`}
          >
            {initials(u.name)}
          </span>
        ))}
        {overflow > 0 && (
          <span
            title={items.slice(max).map((u) => u.name).join(', ')}
            className={`inline-flex ${dot} rounded-full items-center justify-center font-bold text-slate-200 bg-slate-500/30 border border-[#0a1628] ring-1 ring-slate-500/40`}
          >
            +{overflow}
          </span>
        )}
      </div>
    </div>
  );
}
